import { Box, Flex, Text } from '@chakra-ui/react';
import { faBell, faClock } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Accordion, Badge, ButtonText } from '@morressier/alembic';
import { useState } from 'react';
import { calculateDaysToDeadline } from 'src/utils/deadlines';
import { RequestAssessmentDeadlineExtensionModal } from './RequestAssessmentDeadlineExtensionModal';
import { IconBadge } from 'src/components/IconBadge';
import { useAssessmentData } from 'src/contexts/AssessmentDataProvider';
import { faPen } from '@fortawesome/pro-light-svg-icons';
import NoteFromEditorModal from 'src/components/Assessment/NoteFromEditorModal';
import AdditionalInformationWrapper from '../../AdditionalInformationWrapper';
import { useAdjudicationData } from 'src/contexts/AdjudicationDataProvider';
import { timeFormatDeadlineDate } from '../../../../../utils/date';

const getData = (entityType: string) => {
  if (entityType === 'adjudication') {
    const {
      adjudication,
      hasSubmittedAdjudicationInCurrentRound,
      availableActions
    } = useAdjudicationData();
    return {
      data: adjudication,
      hasSubmittedDataInCurrentRound: hasSubmittedAdjudicationInCurrentRound,
      availableActions
    };
  } else {
    const {
      earlyAssessment,
      hasSubmittedAssessmentInCurrentRound,
      availableActions
    } = useAssessmentData();
    return {
      data: earlyAssessment,
      hasSubmittedDataInCurrentRound: hasSubmittedAssessmentInCurrentRound,
      availableActions
    };
  }
};

export const ExpertPeerReviewAdditionalInfo = ({
  entityType
}: {
  entityType: string;
}) => {
  const { data, hasSubmittedDataInCurrentRound, availableActions } =
    getData(entityType);
  const [isExtensionRequestedModal, setIsExtensionRequestedModal] =
    useState<boolean>(false);
  const [isNoteFromEditorModal, setIsNoteFromEditorModal] = useState(false);

  const reviewDeadline = new Date(data.workflow.currentDeadline ?? '2024');
  const daysToDeadline = calculateDaysToDeadline(reviewDeadline);
  const extensionRequested =
    data.workflow.deadlineExtensions?.status === 'requested';

  return (
    <AdditionalInformationWrapper>
      <NoteFromEditorModal
        onClose={() => setIsNoteFromEditorModal(false)}
        isOpen={isNoteFromEditorModal}
        editorialRequest={data?.caseData?.editorialRequest}
      />
      {isExtensionRequestedModal && (
        <RequestAssessmentDeadlineExtensionModal
          submissionId={data.caseData.submission.id}
          submissionContextId={data.submissionContextId}
          onClose={() => setIsExtensionRequestedModal(false)}
        />
      )}

      <Accordion
        chakraAccordionProps={{
          width: '100%',
          allowMultiple: true,
          defaultIndex: [0]
        }}
        items={[
          {
            title: (
              <Flex alignItems={'flex-start'} gap={2}>
                <IconBadge icon={faBell} variant="neutral" />
                <Flex flexDirection={'column'} marginLeft={2}>
                  <Text textStyle="headingItem">Highlights</Text>
                  <Text textStyle="textContext">
                    Review key updates and other relevant information in this
                    phase.
                  </Text>
                </Flex>
              </Flex>
            ),
            content: (
              <>
                {availableActions.contains(
                  'RequestReviewDeadlineExtension'
                ) && (
                  <Flex alignItems="center" gap={4}>
                    <Badge
                      variant="attention"
                      size="context"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                    >
                      <Flex paddingX={1}>
                        <FontAwesomeIcon icon={faClock} />
                      </Flex>
                    </Badge>
                    <Flex flex={1} flexDirection="column">
                      <Text textStyle="headingItem">
                        Deadline on {timeFormatDeadlineDate(reviewDeadline)}
                      </Text>
                      <Text textStyle="textBody">
                        {data.workflow.deadlineExtensions?.status === 'denied'
                          ? 'The editors have rejected your last extension request.'
                          : `You have ${daysToDeadline} days to submit your review.`}
                      </Text>
                    </Flex>

                    {extensionRequested ? (
                      <Badge>
                        <Text textStyle="headingItem">Extension requested</Text>
                      </Badge>
                    ) : (
                      <ButtonText
                        onClick={() => setIsExtensionRequestedModal(true)}
                        isDisabled={hasSubmittedDataInCurrentRound()}
                      >
                        Request Extension
                      </ButtonText>
                    )}
                  </Flex>
                )}

                <Flex alignItems="flex-start" gap={4} marginY={3}>
                  <Badge
                    variant="attention"
                    size="context"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Flex paddingX={1}>
                      <FontAwesomeIcon
                        icon={faPen}
                        style={{ height: '24px', width: '16px' }}
                      />
                    </Flex>
                  </Badge>
                  <Box>
                    <Text textStyle="headingItem">Note from the Editor</Text>
                    <Text>
                      The Editor left actionable feedback to guide you through
                      this assessment.
                    </Text>
                  </Box>

                  <ButtonText
                    onClick={() => setIsNoteFromEditorModal(true)}
                    disabled={!data.caseData.editorialRequest}
                  >
                    View Note
                  </ButtonText>
                </Flex>
              </>
            )
          }
        ]}
      />
    </AdditionalInformationWrapper>
  );
};
